import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

const MusicPlayerContext = createContext(null);

export function useMusicPlayer() {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  }
  return context;
}

export function MusicPlayerProvider({ children }) {
  const audioRef = useRef(new Audio());
  const [currentTrack, setCurrentTrack] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('musicVolume');
    return saved ? parseFloat(saved) : 1;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off'); // off, all, one
  const [isShuffled, setIsShuffled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => setProgress(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => handleTrackEndRef.current?.();
    const handleError = () => {
      setError('Failed to load audio');
      setIsLoading(false);
      setIsPlaying(false);
    };
    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
    };
  }, []);

  // Update volume
  useEffect(() => {
    audioRef.current.volume = isMuted ? 0 : volume;
    localStorage.setItem('musicVolume', volume.toString());
  }, [volume, isMuted]);

  const handleTrackEndRef = useRef(null);

  const playTrack = useCallback((track, newQueue = null) => {
    setError(null);
    setIsLoading(true);

    if (newQueue) {
      setQueue(newQueue);
      const index = newQueue.findIndex(t => t.id === track.id);
      setQueueIndex(index >= 0 ? index : 0);
    }

    setCurrentTrack(track);
    audioRef.current.src = apiService.getMusicStreamUrl(track.id);
    audioRef.current.play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        console.error('Play error:', err);
        setError('Failed to play audio');
        setIsPlaying(false);
        setIsLoading(false);
      });
  }, []);

  const playTrackAtIndex = useCallback((index) => {
    if (index >= 0 && index < queue.length) {
      setQueueIndex(index);
      playTrack(queue[index]);
    }
  }, [queue, playTrack]);

  const togglePlayPause = useCallback(() => {
    if (!currentTrack) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(console.error);
    }
  }, [currentTrack, isPlaying]);

  const playNext = useCallback(() => {
    if (queue.length === 0) return;

    let nextIndex;
    if (isShuffled) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else {
      nextIndex = queueIndex + 1;
      if (nextIndex >= queue.length) {
        nextIndex = repeatMode === 'all' ? 0 : queueIndex;
      }
    }

    if (nextIndex !== queueIndex || repeatMode === 'all') {
      playTrackAtIndex(nextIndex);
    }
  }, [queue.length, queueIndex, isShuffled, repeatMode, playTrackAtIndex]);

  const handleTrackEnd = useCallback(() => {
    if (repeatMode === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else if (queueIndex < queue.length - 1) {
      playNext();
    } else if (repeatMode === 'all' && queue.length > 0) {
      playTrackAtIndex(0);
    } else {
      setIsPlaying(false);
    }
  }, [repeatMode, queueIndex, queue.length, playNext, playTrackAtIndex]);

  // Keep ref in sync so the event listener always calls the latest version
  useEffect(() => {
    handleTrackEndRef.current = handleTrackEnd;
  }, [handleTrackEnd]);

  const playPrevious = useCallback(() => {
    if (queue.length === 0) return;

    // If more than 3 seconds in, restart current track
    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    let prevIndex = queueIndex - 1;
    if (prevIndex < 0) {
      prevIndex = repeatMode === 'all' ? queue.length - 1 : 0;
    }
    playTrackAtIndex(prevIndex);
  }, [queue.length, queueIndex, repeatMode, playTrackAtIndex]);

  const seek = useCallback((time) => {
    audioRef.current.currentTime = time;
    setProgress(time);
  }, []);

  const seekPercent = useCallback((percent) => {
    const time = (percent / 100) * duration;
    seek(time);
  }, [duration, seek]);

  const setVolumeLevel = useCallback((newVolume) => {
    setVolume(Math.max(0, Math.min(1, newVolume)));
    if (newVolume > 0) setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const toggleRepeat = useCallback(() => {
    setRepeatMode(prev => {
      if (prev === 'off') return 'all';
      if (prev === 'all') return 'one';
      return 'off';
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    setIsShuffled(prev => !prev);
  }, []);

  const addToQueue = useCallback((track) => {
    setQueue(prev => {
      const newQueue = [...prev, track];
      // If nothing is playing yet, start this track immediately
      if (!currentTrack) {
        const index = newQueue.length - 1;
        setQueueIndex(index);
        setCurrentTrack(track);
        setError(null);
        setIsLoading(true);
        audioRef.current.src = apiService.getMusicStreamUrl(track.id);
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(() => {
            setError('Failed to play audio');
            setIsPlaying(false);
            setIsLoading(false);
          });
      }
      return newQueue;
    });
  }, [currentTrack]);

  const removeFromQueue = useCallback((index) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
    if (index < queueIndex) {
      setQueueIndex(prev => prev - 1);
    } else if (index === queueIndex && queue.length > 1) {
      // If removing current track, play next
      if (index < queue.length - 1) {
        playTrackAtIndex(index);
      } else {
        playTrackAtIndex(index - 1);
      }
    }
  }, [queueIndex, queue.length, playTrackAtIndex]);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setQueueIndex(-1);
    setCurrentTrack(null);
    audioRef.current.pause();
    audioRef.current.src = '';
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
  }, []);

  const playAlbum = useCallback((tracks, startIndex = 0) => {
    if (tracks.length === 0) return;
    setQueue(tracks);
    setQueueIndex(startIndex);
    playTrack(tracks[startIndex], tracks);
  }, [playTrack]);

  const value = {
    // State
    currentTrack,
    queue,
    queueIndex,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    repeatMode,
    isShuffled,
    isLoading,
    error,
    // Actions
    playTrack,
    togglePlayPause,
    playNext,
    playPrevious,
    seek,
    seekPercent,
    setVolumeLevel,
    toggleMute,
    toggleRepeat,
    toggleShuffle,
    addToQueue,
    removeFromQueue,
    clearQueue,
    playAlbum,
    playTrackAtIndex,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
}

export default MusicPlayerProvider;
